import React, { useState, useEffect } from "react";
import {
  Users,
  UserPlus,
  Trash2,
  Edit2,
  Shield,
  MapPin,
  X,
  Key,
  ShieldCheck,
  Link2Off,
} from "lucide-react";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [activeTab, setActiveTab] = useState("user");

  // UI States
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Form States
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "user",
  });
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    username: "",
    password: "",
    siteMac: "",
  });

  const currentUserRole = localStorage.getItem("role");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("userToken");
    try {
      // Fetch Users
      const userRes = await fetch(
        "https://db2sg.com/tagweigh/api/auth?action=list",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const userData = await userRes.json();
      setUsers(userData.users || []);

      // Fetch Sites
      const siteRes = await fetch(
        `https://db2sg.com/tagweigh/api/sites?t=${Date.now()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const siteData = await siteRes.json();
      setSites(siteData || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("userToken");
    try {
      const res = await fetch(
        "https://db2sg.com/tagweigh/api/auth?action=register",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...formData, role: activeTab }),
        }
      );
      if (res.ok) {
        alert("Account Created");
        setShowAddModal(false);
        setFormData({ username: "", password: "" });
        fetchData();
      }
    } catch (err) {
      alert("Registration failed");
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    const assignedSite = sites.find((s) => s.user === user.id);
    setEditFormData({
      username: user.username,
      password: "",
      siteMac: assignedSite ? assignedSite.mac : "",
    });
    setIsEditing(true);
  };

  const handleUpdate = async () => {
    const token = localStorage.getItem("userToken");
    try {
      // Update Auth Info
      await fetch("https://db2sg.com/tagweigh/api/auth?action=update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: editingUser.id,
          username: editFormData.username,
          password: editFormData.password || undefined,
        }),
      });

      // Update Site Assignment (only for 'user' role)
      if (editingUser.role === "user" && editFormData.siteMac) {
        await fetch("https://db2sg.com/tagweigh/api/sites?updateUser=1", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            mac: editFormData.siteMac,
            user: editingUser.id.toString(),
          }),
        });
      }

      setIsEditing(false);
      fetchData();
      alert("Updated successfully");
    } catch (err) {
      alert("Update failed");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    const token = localStorage.getItem("userToken");
    try {
      const res = await fetch(
        `https://db2sg.com/tagweigh/api/auth?action=delete&id=${id}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) setUsers(users.filter((u) => u.id !== id));
    } catch (err) {
      alert("Delete failed");
    }
  };

  const availableSites = sites.filter(
    (s) => !s.user || s.user === "0" || s.user === editingUser?.id
  );
  const handleUnassign = async (siteMac) => {
    const token = localStorage.getItem("userToken");
    try {
      await fetch("https://db2sg.com/tagweigh/api/sites?updateUser=1", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mac: siteMac }), // "0" removes the link
      });
      fetchData(); // Refresh UI
    } catch (err) {
      alert("Unassign failed");
    }
  };
  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row justify-between items-end gap-4  border-slate-200 pb-0">
        {currentUserRole === "admin" && (
          <div className="flex items-center justify-between bg-white p-2 rounded-3xl border border-slate-200 shadow-sm w-fit">
            {["technician", "user"].map((role) => (
              <button
                key={role}
                onClick={() => setActiveTab(role)}
                className={`px-10 py-3 rounded-2xl font-black transition-all duration-300 ${
                  activeTab === role
                    ? "bg-emerald-500 text-white shadow-lg shadow-emerald-200"
                    : "text-slate-400 hover:text-slate-600"
                }`}
              >
                {role.charAt(0).toUpperCase() + role.slice(1)}s
              </button>
            ))}
          </div>
        )}
        <button
          onClick={() => setShowAddModal(true)}
          className="group flex items-center gap-3 bg-[#0F172A] text-white px-8 py-4 rounded-2xl font-black hover:bg-emerald-600 transition-all shadow-xl shadow-emerald-500/10 active:scale-95"
        >
          <UserPlus
            size={20}
            className="group-hover:rotate-12 transition-transform"
          />
          ADD NEW
        </button>
      </div>

      {/* USER CARDS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {users
          .filter((u) => u.role === activeTab)
          .map((user) => {
            const assignedSite = sites.find((s) => s.user === user.id);

            return (
              <div
                key={user.id}
                className="bg-white rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-xl hover:border-emerald-200 transition-all duration-300 group overflow-hidden"
              >
                {/* Card Header */}
                <div className="p-6 pb-0 flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-slate-50 border border-slate-100 text-slate-900 rounded-2xl flex items-center justify-center font-black text-xl group-hover:bg-emerald-50 group-hover:text-emerald-600 transition-colors">
                      {user.username[0].toUpperCase()}
                    </div>
                    <div>
                      <h4 className="font-black text-slate-800 text-lg leading-tight truncate max-w-[180px]">
                        {user.username}
                      </h4>
                      <div className="flex items-center gap-1.5 mt-1">
                        <Shield size={12} className="text-emerald-500" />
                        <span className="text-[10px] font-black text-emerald-600 uppercase tracking-tighter">
                          {user.role}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(user.id, user.username)}
                    className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {/* Site Assignment Info */}
                <div className="px-6 py-6">
                  <div
                    className={`rounded-2xl p-4 border transition-colors ${
                      assignedSite
                        ? "bg-emerald-50/50 border-emerald-100"
                        : "bg-slate-50 border-slate-100"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        Assigned Site
                      </span>
                      {assignedSite && (
                        <button
                          onClick={() => handleUnassign(assignedSite.mac)}
                          className="text-red-400 hover:text-red-600 flex items-center gap-1 transition-colors"
                          title="Unassign Site"
                        >
                          <Link2Off size={14} />
                          <span className="text-[10px] font-bold">
                            Disconnect
                          </span>
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin
                        size={14}
                        className={
                          assignedSite ? "text-emerald-500" : "text-slate-300"
                        }
                      />
                      <span
                        className={`font-bold text-sm ${
                          assignedSite
                            ? "text-slate-700"
                            : "text-slate-400 italic"
                        }`}
                      >
                        {assignedSite
                          ? assignedSite.name || assignedSite.mac
                          : "No Site Assigned"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Footer Actions */}
                <div className="px-6 pb-6 mt-auto">
                  <button
                    onClick={() => handleEditClick(user)}
                    className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white py-3.5 rounded-2xl font-black hover:bg-emerald-600 transition-all shadow-lg active:scale-95"
                  >
                    <Edit2 size={16} /> EDIT PROFILE
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* EDIT MODAL (RESTYLED) */}
      {isEditing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-300">
            <div className="bg-slate-50 px-8 py-6 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-black italic text-slate-900">
                  MODIFY ACCOUNT
                </h3>
                <p className="text-xs font-bold text-slate-400 uppercase">
                  ID: {editingUser.id}
                </p>
              </div>
              <button
                onClick={() => setIsEditing(false)}
                className="p-2 hover:bg-white rounded-xl shadow-sm transition-all"
              >
                <X />
              </button>
            </div>

            <div className="p-8 space-y-5">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase ml-1">
                  Account Username
                </label>
                <div className="relative">
                  <Users
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    size={18}
                  />
                  <input
                    className="w-full bg-slate-50 pl-12 pr-4 py-4 rounded-2xl font-bold outline-none border-2 border-transparent focus:border-emerald-500 focus:bg-white transition-all"
                    value={editFormData.username}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        username: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase ml-1">
                  Change Password
                </label>
                <div className="relative">
                  <Key
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    size={18}
                  />
                  <input
                    type="password"
                    placeholder="••••••••"
                    className="w-full bg-slate-50 pl-12 pr-4 py-4 rounded-2xl font-bold outline-none border-2 border-transparent focus:border-emerald-500 focus:bg-white transition-all"
                    value={editFormData.password}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        password: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              {editingUser.role === "user" && (
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase ml-1">
                    Network Site Assignment
                  </label>
                  <div className="relative">
                    <MapPin
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                      size={18}
                    />
                    <select
                      className="w-full bg-slate-50 pl-12 pr-4 py-4 rounded-2xl font-bold outline-none border-2 border-transparent focus:border-emerald-500 appearance-none transition-all"
                      value={editFormData.siteMac}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          siteMac: e.target.value,
                        })
                      }
                    >
                      <option value="">( Unassigned )</option>
                      {availableSites.map((s) => (
                        <option key={s.mac} value={s.mac}>
                          {s.name || s.mac}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="pt-4 flex gap-3">
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 bg-slate-100 text-slate-600 py-4 rounded-2xl font-black hover:bg-slate-200 transition-all"
                >
                  CANCEL
                </button>
                <button
                  onClick={handleUpdate}
                  className="flex-[2] bg-emerald-500 text-white py-4 rounded-2xl font-black hover:bg-emerald-600 shadow-lg shadow-emerald-200 transition-all flex items-center justify-center gap-2"
                >
                  <ShieldCheck size={20} /> UPDATE ACCESS
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-md p-8">
            <h3 className="text-2xl font-black mb-8 italic uppercase">
              NEW {activeTab}
            </h3>
            <form onSubmit={handleRegister} className="space-y-6">
              <input
                placeholder="Username"
                className="w-full bg-slate-50 p-4 rounded-2xl font-bold outline-none"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                required
              />
              <input
                type="password"
                placeholder="Password"
                className="w-full bg-slate-50 p-4 rounded-2xl font-bold outline-none"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required
              />
              <button className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black">
                CREATE ACCOUNT
              </button>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="w-full text-red-500 font-bold"
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
